/**
 * TEMPORARY — sc-624 parity admin tool.
 * Delete after typed-tables migration cutover.
 */

import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs'
import { ParityForecastsTable } from './-components/ParityForecastsTable'
import { ParityListFilters } from './-components/ParityListFilters'
import { PageTitle } from '@/components/PageTitle'
import { useParityForecasts } from '@/hooks/repositories/useParityRepository'
import { useAllClients } from '@/hooks/repositories/useClientsRepository'
import { useAuth } from '@/contexts/AuthContext'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

export const Route = createFileRoute('/_app/backoffice/admin/parity/')({
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.devTesting),
})

const PAGE_SIZE = 20

function RouteComponent() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))
  const [clientId, setClientId] = useQueryState('clientId', parseAsString)
  const [status, setStatus] = useQueryState('status', parseAsString)

  // Reset to page 1 when filters change
  const handleClientIdChange = React.useCallback(
    (id: string | null) => {
      void setPage(1)
      void setClientId(id)
    },
    [setPage, setClientId],
  )

  const handleStatusChange = React.useCallback(
    (s: string | null) => {
      void setPage(1)
      void setStatus(s)
    },
    [setPage, setStatus],
  )

  const { data, isLoading } = useParityForecasts({
    page,
    perPage: PAGE_SIZE,
    clientId: clientId ?? undefined,
    status: (status as 'PENDING' | 'VERIFIED' | 'FLAGGED' | null) ?? undefined,
  })

  const {
    data: allClients = {
      items: [],
      total: 0,
      page: 1,
      perPage: 50,
      totalPages: 1,
    },
  } = useAllClients(user?.databaseId, { perPage: 200 })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  const currentPage = data?.page ?? 1

  return (
    <div className="py-4">
      <PageTitle title="Parity Review (temp)" />
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight">Parity Review</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Temporary sc-624 tool — verify PDF parity between JSON and
          typed-tables report paths.
        </p>
      </div>

      <div className="space-y-3">
        <ParityListFilters
          clientId={clientId ?? null}
          clients={allClients.items}
          onClientChange={handleClientIdChange}
          status={status ?? null}
          onStatusChange={handleStatusChange}
        />

        <ParityForecastsTable
          data={items}
          isLoading={isLoading}
          pagination={{
            currentPage,
            totalPages,
            total,
            perPage: PAGE_SIZE,
            onPageChange: (p) => void setPage(p),
          }}
          onRowClick={(forecastId) =>
            void navigate({
              to: '/backoffice/admin/parity/$forecastId',
              params: { forecastId },
            })
          }
        />
      </div>
    </div>
  )
}
